import "@measured/puck/puck.css";

import createEmotionCache from "@measured/puck-plugin-emotion-cache";
import { Data, Puck } from "@measured/puck";
import { useState, useEffect, useRef, useCallback } from "react";
import { Navigate, useParams, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { Box, Button, useTheme, Dialog } from "@mui/material";
import { FoodsIcon } from "@foodpilot/foods";
import { isEqual } from "lodash";

import { usePuckExtension, useUpdatePuckExtension } from "src/components/PuckExtensions";
import { PuckRenderer } from "src/components/Puck/PuckRenderer";
import { Props } from "src/components/Puck/config";
import { RootProps } from "src/components/Puck/config/root";
import { rootConfig } from "src/components/Puck/config";
import { useSnackbar } from "src/utils/useSnackbar";
import { Loading } from "src/components/utils/Loading";
import { useLeaveDetection } from "src/utils/useLeaveDetection";
import { EditorLeaveConfirmation } from "src/components/Puck/components/EditorLeaveConfirmation";
import { IframeStyleInjector } from "src/components/Puck/iframe/IframeStyleInjector";
import { PuckEditorProviders } from "src/components/Puck/editor/PuckEditorProviders";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { URLHandler } from "src/services/URLHandler";
import { parseDynaBlockExtensionId } from "./dynablockRoute";

export const DynaBlockEditor = () => {
    const { t } = useTranslation();
    const { extensionId } = useParams<{ extensionId: string }>();
    const [data, setData] = useState<Data<Props, RootProps> | null>(null);
    const [iframeStylesLoaded, setIframeStylesLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isExtensionEdited, setIsExtensionEdited] = useState(false);
    const [showLeaveConfirmation, setShowLeaveConfirmation] = useState(false);
    const [isSavingBeforeQuit, setIsSavingBeforeQuit] = useState(false);
    const [recentlySaved, setRecentlySaved] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewData, setPreviewData] = useState<Data<Props, RootProps> | null>(null);

    const currentDataRef = useRef<Data<Props, RootProps> | null>(null);
    const initialDataRef = useRef<Data<Props, RootProps> | null>(null);
    const iframeRef = useRef<HTMLIFrameElement | null>(null);

    const snackbar = useSnackbar();
    const theme = useTheme();
    const navigate = useNavigate();
    const foodpilotContext = useFoodpilotContext();
    const companyId = foodpilotContext?.selectedCompany?.id;
    const parsedExtensionId = parseDynaBlockExtensionId(extensionId);
    const { data: extension, isFetched: isExtensionFetched } = usePuckExtension(parsedExtensionId ?? 0, {
        enabled: parsedExtensionId !== null,
    });

    // Update mutation
    const { isLoading: isExtensionUpdateLoading, mutate: updateExtensionMutate } = useUpdatePuckExtension();

    const isError = !extension && isExtensionFetched;

    const handleLeaveAttempt = useCallback(() => {
        if (isExtensionEdited && !recentlySaved) {
            setShowLeaveConfirmation(true);
        } else {
            navigate(URLHandler.settings.cms.dynablocks());
        }
    }, [isExtensionEdited, recentlySaved, navigate]);

    // Détection des tentatives de quitter
    useLeaveDetection(isExtensionEdited && !recentlySaved, () => setShowLeaveConfirmation(true));

    useEffect(() => {
        if (extension && isExtensionFetched) {
            const defaultData: Data<Props, RootProps> = { content: [], root: { props: {} } };
            let initialData: Data<Props, RootProps> = defaultData;
            try {
                if (extension.puckData) {
                    initialData = JSON.parse(extension.puckData) as Data<Props, RootProps>;
                }
            } catch {
                initialData = defaultData;
            }

            setData(initialData);
            currentDataRef.current = initialData;
            initialDataRef.current = initialData;

            setTimeout(() => {
                setIframeStylesLoaded(true);
            }, 100);
        }
    }, [extension, isExtensionFetched]);

    const handleDataChange = (newData: Data<Props, RootProps>) => {
        currentDataRef.current = newData;
        const hasChanges = !isEqual(newData, initialDataRef.current);

        if (hasChanges !== isExtensionEdited && !recentlySaved) {
            setIsExtensionEdited(hasChanges);
        }
    };

    const handlePublish = (newData: Data<Props, RootProps> | null, onComplete?: () => void) => {
        if (!extension || !newData) return;

        updateExtensionMutate(
            {
                id: extension.id,
                puckData: JSON.stringify(newData),
            },
            {
                onSuccess: () => {
                    snackbar.forceClose();
                    snackbar.setSnackbarProps({
                        type: "success",
                        message: t("settings.cms.dynablocks.save_success", "DynaBlock saved successfully"),
                    });
                    initialDataRef.current = currentDataRef.current;
                    setIsExtensionEdited(false);
                    setRecentlySaved(true);
                    setTimeout(() => setRecentlySaved(false), 2000);

                    if (onComplete) onComplete();
                },
                onError: () => {
                    snackbar.forceClose();
                    snackbar.setSnackbarProps({
                        type: "error",
                        message: t("settings.cms.dynablocks.save_error", "Failed to save DynaBlock"),
                    });
                },
            },
        );
    };

    const handlePreview = () => {
        if (currentDataRef.current) {
            setPreviewData(currentDataRef.current);
            setPreviewOpen(true);
        }
    };

    const handleClosePreview = () => {
        setPreviewOpen(false);
    };

    const handleBackToDynaBlockManager = useCallback(() => {
        setIsExtensionEdited(false);
        setTimeout(() => {
            navigate(URLHandler.settings.cms.dynablocks());
        }, 0);
    }, [navigate]);

    useEffect(() => {
        setIsLoading(!isExtensionFetched || (!!extension && !iframeStylesLoaded));
    }, [extension, isExtensionFetched, iframeStylesLoaded]);

    if (parsedExtensionId === null) {
        return <Navigate replace to={URLHandler.settings.cms.dynablocks()} />;
    }

    if (isLoading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                    width: "100%",
                }}
            >
                <Loading />
                <Box sx={{ mt: theme.spacing(2) }}>
                    {!isExtensionFetched ?
                        "Loading DynaBlock..."
                    : !iframeStylesLoaded ?
                        "Preparing editor styles..."
                    :   "Initializing editor..."}
                </Box>
            </Box>
        );
    }

    if (isError || !extension) {
        return (
            <Box
                sx={{ height: "100vh", width: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}
            >
                <Box sx={{ textAlign: "center" }}>
                    <FoodsIcon icon="warning" size={4} />
                    <Box sx={{ mt: 2 }}>{t("settings.cms.dynablocks.not_found", "DynaBlock not found")}</Box>
                </Box>
            </Box>
        );
    }

    const muiEmotionCache = createEmotionCache("puck-app");

    const renderEditor = data && (
        <Box sx={{ height: "100%", width: "100%", zIndex: 1 }}>
            <Puck
                config={rootConfig}
                data={data}
                onChange={handleDataChange}
                headerTitle={extension?.name || ""}
                headerPath={`DynaBlock: ${extension?.extensionPoint || ""}`}
                plugins={[muiEmotionCache]}
                overrides={{
                    iframe: ({ children, document: iFrameDoc }) => {
                        const handleIframeRef = (node: HTMLIFrameElement | null) => {
                            if (node && !iframeRef.current) {
                                iframeRef.current = node;
                            }
                        };

                        return (
                            <div ref={handleIframeRef}>
                                <IframeStyleInjector document={iFrameDoc}>{children}</IframeStyleInjector>
                            </div>
                        );
                    },
                    headerActions: () => {
                        return (
                            <Box sx={{ display: "flex", gap: theme.spacing(2) }}>
                                <Box sx={{ flexGrow: 1 }} />
                                <Button
                                    variant="ternary"
                                    onClick={handleLeaveAttempt}
                                    startIcon={<FoodsIcon size={2} icon="arrowLeft" />}
                                >
                                    {t("settings.cms.dynablocks.return_to_manager", "Return to Manager")}
                                </Button>
                                <Button
                                    variant="ternary"
                                    onClick={handlePreview}
                                    startIcon={<FoodsIcon size={2} icon="viewPwd" />}
                                >
                                    {t("settings.cms.dynablocks.preview", "Preview")}
                                </Button>
                                <Button
                                    variant="primary"
                                    disabled={isExtensionUpdateLoading || !isExtensionEdited}
                                    onClick={() => handlePublish(currentDataRef.current)}
                                    startIcon={
                                        isExtensionUpdateLoading ?
                                            <Loading size={14} />
                                        :   <FoodsIcon size={2} icon="save" />
                                    }
                                >
                                    {t("settings.cms.dynablocks.save", "Save")}
                                </Button>
                            </Box>
                        );
                    },
                }}
            />
            <Box
                sx={{
                    background: theme.palette.divider,
                    height: "1px",
                    width: "100%",
                }}
            />
        </Box>
    );

    return (
        <>
            <PuckEditorProviders companyId={companyId}>
                <>
                    <Box sx={{ height: "100%", flex: 1 }}>{renderEditor}</Box>

                    <Dialog fullScreen open={previewOpen} onClose={handleClosePreview}>
                        <Box sx={{ position: "relative", height: "100vh", overflow: "auto" }}>
                            <Button
                                variant="ternary"
                                color="primary"
                                sx={{
                                    position: "fixed",
                                    top: 16,
                                    right: 16,
                                    zIndex: 9999,
                                }}
                                onClick={handleClosePreview}
                                startIcon={<FoodsIcon icon="close" size={2} />}
                            >
                                {t("glossary.close", "Close Preview")}
                            </Button>
                            <PuckRenderer data={previewData || {}} isLoading={false} isPreview />
                        </Box>
                    </Dialog>
                </>
            </PuckEditorProviders>

            <EditorLeaveConfirmation
                open={showLeaveConfirmation}
                onCancel={() => {
                    setShowLeaveConfirmation(false);
                }}
                onConfirm={handleBackToDynaBlockManager}
                onSaveAndQuit={() => {
                    setIsSavingBeforeQuit(true);
                    return new Promise<void>((resolve) => {
                        handlePublish(currentDataRef.current, () => {
                            setIsSavingBeforeQuit(false);
                            setIsExtensionEdited(false);
                            setTimeout(() => {
                                navigate(URLHandler.settings.cms.dynablocks());
                                resolve();
                            }, 0);
                        });
                    });
                }}
                isSaving={isSavingBeforeQuit}
            />
        </>
    );
};
